import './Billing.scss'
import React from "react"
import { LabelFormatter, ResponsiveBar } from "@nivo/bar"
import { formatEuros } from "../../shared/string"

export interface ResponsiveBarItem {
    label: string,
    [group: string]: number | string | {sum: number, count: number},
}

export interface BillingResponsiveBarProps<T extends ResponsiveBarItem> {
    data: T[],
    keys: string[],
    groupMode?: 'grouped'|'stacked',
    type?: 'sum'|'count',
    sortByLabel?: boolean,
    labelFormat?: LabelFormatter,
    tooltipFormat?: LabelFormatter,
    onClick?: (id: string, data: T) => void,
}

export const BillingResponsiveBar = <T extends ResponsiveBarItem>({
    data,
    keys,
    groupMode,
    type = 'sum',
    sortByLabel = true,
    labelFormat,
    tooltipFormat,
    onClick,
}: BillingResponsiveBarProps<T>) => {
    const normalizedData = React.useMemo(() => {
        const ordered = sortByLabel ? [...data].sort((a, b) => a.label < b.label ? 1 : -1) : [...data];
        return ordered.map(d => Object.fromEntries(Object.entries(d).map(
            ([k, v]) => typeof v === 'object' && 'sum' in v ? [k, type === 'sum' ? v.sum : v.count] : [k, v]
        ))) as T[];
    }, [data, sortByLabel, type]);

    return (
        <div style={{height:`${150+normalizedData.length*(groupMode === 'grouped' ? 40 : 20)}px`, width: '100%'}}>
            <ResponsiveBar
                data={normalizedData}
                onClick={data => onClick && onClick(data.id as string, data.data as T)}
                keys={keys} 
                labelSkipWidth={65}
                labelSkipHeight={12}
                labelFormat={labelFormat ? labelFormat : type === 'sum' ? formatEuros : undefined}
                tooltipFormat={tooltipFormat ? tooltipFormat : type === 'sum' ? formatEuros : undefined}
                groupMode={groupMode ?? 'stacked'}
                indexBy="label"
                margin={{ 
                    top: 10, 
                    right: 10, 
                    bottom: 120, 
                    left: normalizedData.reduce((curr, item) => curr > (item.label as string).length ? curr : (item.label as string).length, 0) * 5 + 15,
                }}
                padding={0.3}
                colors={{ scheme: 'nivo' }}
                layout="horizontal"
                axisBottom={{ legend: '€', tickValues: 5 }}
                enableGridX={true}
                legends={keys.length > 6 ? undefined : [
                    {
                        dataFrom: 'keys',
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: 60,
                        itemsSpacing: 10,
                        itemWidth: Math.max(...keys.map(k => k.length*0.52*12)) + 20,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                            {
                                on: 'hover',
                                style: {
                                    itemOpacity: 1
                                }
                            }
                        ]
                    }
                ]}
            />
        </div>
    )
}
